import React, { useState } from 'react';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/extensions
import { IoOpenOutline } from 'react-icons/io5';
// eslint-disable-next-line import/extensions
import { FaRegCopy } from 'react-icons/fa';
import CheckBox from 'rc-checkbox';
import pearlUrl from '../../config/pearl-url.js';
import { blue } from '../../styles/standard-colors.js';

function ticketFailsAudit(flags) {
    if (!flags) return false;
    return !!(
        flags.lowSurvey ||
        flags.initialResponseSla ||
        flags.scFollowupSla ||
        flags.closedOutsideDueSla ||
        flags.prematureClosure ||
        flags.dueDateEdited
    );
}

function summarizeSla(flags) {
    if (!flags) return '—';
    const parts = [];
    if (flags.initialResponseSla) parts.push('Initial response SLA');
    if (flags.scFollowupSla) parts.push('SC follow-up SLA');
    if (flags.closedOutsideDueSla) parts.push('Closed past due');
    if (flags.prematureClosure) parts.push('Premature close');
    if (flags.dueDateEdited) parts.push('Due edited');
    if (flags.lowSurvey) parts.push('Low survey');
    return parts.length ? parts.join(' · ') : 'No SLA flags';
}

function truncate(text, max) {
    const s = (text || '').trim();
    if (!s) return '—';
    if (s.length <= max) return s;
    return `${s.slice(0, max)}…`;
}

/**
 * One audit ticket row (dashboard table layout). Ticket ID links to Pearl.
 *
 * @param {object} props
 * @param {object} props.ticket
 * @param {boolean} [props.showTechColumn]
 * @param {boolean} [props.flaggedForTechReview]
 * @param {(ticketKey: string, checked: boolean) => void} [props.onFlaggedForTechReviewChange]
 */
const TicketCard = ({
    ticket,
    showTechColumn = false,
    flaggedForTechReview = false,
    onFlaggedForTechReviewChange
}) => {
    const [copySuccess, setCopySuccess] = useState(false);
    const flags = ticket.auditFlags;
    const autoFail = ticketFailsAudit(flags);
    const humanId = String(ticket.humanId || '').trim() || '—';
    const rowKey = String(ticket.humanId || ticket._id || '').trim() || humanId;
    const pearlPath = ticket._id
        ? `${pearlUrl}/chat/closed/${ticket._id}`
        : `${pearlUrl}/search`;
    const summaryRaw =
        (ticket.userText && String(ticket.userText).trim()) ||
        (ticket.officeSysName &&
            `Office: ${String(ticket.officeSysName).trim()}`) ||
        (ticket.type && String(ticket.type).trim()) ||
        '';
    const summary = summaryRaw ? truncate(summaryRaw, 140) : '—';

    const handleCopy = () => {
        navigator.clipboard
            .writeText(humanId)
            .then(() => {
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 2000);
            })
            .catch((err) => {
                console.error('Error copying text: ', err);
            });
    };

    const rowClass = [
        'audit-ticket-row',
        (autoFail || flaggedForTechReview) && 'audit-ticket-row--attention',
        flaggedForTechReview && 'audit-ticket-row--manual-flag'
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={rowClass} role="row">
            {copySuccess && (
                <span className="audit-ticket-row__copy-tooltip">Copied!</span>
            )}
            {showTechColumn ? (
                <div
                    className="audit-ticket-row__cell audit-ticket-row__tech"
                    role="cell">
                    {ticket.auditPearlTechName || '—'}
                </div>
            ) : null}
            <div
                className="audit-ticket-row__cell audit-ticket-row__id"
                role="cell">
                <a
                    href={pearlPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="audit-ticket-row__id-link"
                    title={humanId !== '—' ? humanId : undefined}>
                    {humanId}
                </a>
                <FaRegCopy
                    className="audit-ticket-row__id-copy"
                    onClick={handleCopy}
                    color={blue}
                    title="Copy ticket ID"
                />
            </div>
            <div
                className="audit-ticket-row__cell audit-ticket-row__summary"
                role="cell"
                title={summaryRaw || undefined}>
                {summary}
            </div>
            <div
                className="audit-ticket-row__cell audit-ticket-row__passfail"
                role="cell">
                <span
                    className={
                        autoFail
                            ? 'audit-ticket-row__badge audit-ticket-row__badge--fail'
                            : 'audit-ticket-row__badge audit-ticket-row__badge--pass'
                    }>
                    {autoFail ? 'Fail' : 'Pass'}
                </span>
                {flags?.surveySatisfaction != null ? (
                    <span className="audit-ticket-row__survey">
                        Survey {flags.surveySatisfaction}/10
                    </span>
                ) : null}
            </div>
            <div
                className="audit-ticket-row__cell audit-ticket-row__sla"
                role="cell">
                {summarizeSla(flags)}
            </div>
            <div
                className="audit-ticket-row__cell audit-ticket-row__pearl"
                role="cell">
                <a
                    className="audit-ticket-row__pearl-link"
                    href={pearlPath}
                    target="_blank"
                    rel="noopener noreferrer">
                    Open in Pearl
                    <IoOpenOutline aria-hidden />
                </a>
            </div>
            <div
                className="audit-ticket-row__cell audit-ticket-row__review"
                role="cell">
                <label className="audit-ticket-row__review-label">
                    <CheckBox
                        checked={flaggedForTechReview}
                        onChange={(e) =>
                            onFlaggedForTechReviewChange?.(
                                rowKey,
                                e.target.checked
                            )
                        }
                    />
                    <span className="audit-ticket-row__review-text">
                        Flag for review
                    </span>
                </label>
            </div>
        </div>
    );
};

TicketCard.propTypes = {
    ticket: PropTypes.object.isRequired,
    showTechColumn: PropTypes.bool,
    flaggedForTechReview: PropTypes.bool,
    onFlaggedForTechReviewChange: PropTypes.func
};

export default TicketCard;
